/**
 * HVAC Graph Store
 *
 * Zustand store for managing the HVAC node-graph state with undo/redo support.
 * Handles nodes, edges, selection, and synchronization with the IDF store.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Node, Edge, XYPosition, Connection } from 'reactflow';
import type {
  HVACNodeData,
  HVACEdgeData,
  HVACAction,
  HVACClipboard,
  ConnectionValidation,
  FluidMedium,
} from './types';
import { getComponentDefinition } from './components';

/**
 * Maximum history size for undo/redo
 */
const MAX_HISTORY_SIZE = 50;

/**
 * Generate a unique node ID
 */
function generateNodeId(): string {
  return `node_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate a unique edge ID
 */
function generateEdgeId(): string {
  return `edge_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate a node name for EnergyPlus based on connected components
 */
function generateNodeName(
  sourceName: string,
  targetName: string,
  _medium: FluidMedium
): string {
  return `${sourceName} to ${targetName} Node`;
}

interface HVACState {
  /** React Flow nodes */
  nodes: Node<HVACNodeData>[];

  /** React Flow edges */
  edges: Edge<HVACEdgeData>[];

  /** Currently selected node IDs */
  selectedNodeIds: string[];

  /** Currently selected edge IDs */
  selectedEdgeIds: string[];

  /** Undo history stack */
  undoStack: HVACAction[];

  /** Redo history stack */
  redoStack: HVACAction[];

  /** Clipboard for copy/paste */
  clipboard: HVACClipboard | null;

  /** Whether the graph has unsaved changes */
  isDirty: boolean;

  /** Node counter for unique naming */
  nodeCounter: Map<string, number>;

  // Actions

  /** Add a new node to the graph */
  addNode: (componentType: string, position: XYPosition, name?: string) => string;

  /** Remove a node from the graph */
  removeNode: (nodeId: string) => void;

  /** Update node position */
  updateNodePosition: (nodeId: string, position: XYPosition) => void;

  /** Update node data */
  updateNodeData: (nodeId: string, data: Partial<HVACNodeData>) => void;

  /** Update multiple node positions (for drag) */
  updateNodePositions: (updates: Array<{ id: string; position: XYPosition }>) => void;

  /** Add a new edge (connection) */
  addEdge: (connection: Connection) => string | null;

  /** Remove an edge */
  removeEdge: (edgeId: string) => void;

  /** Validate a potential connection */
  validateConnection: (connection: Connection) => ConnectionValidation;

  /** Select nodes */
  selectNodes: (nodeIds: string[]) => void;

  /** Select edges */
  selectEdges: (edgeIds: string[]) => void;

  /** Clear selection */
  clearSelection: () => void;

  /** Delete selected elements */
  deleteSelected: () => void;

  /** Copy selected elements to clipboard */
  copySelected: () => void;

  /** Paste from clipboard */
  paste: (position?: XYPosition) => void;

  /** Undo last action */
  undo: () => void;

  /** Redo last undone action */
  redo: () => void;

  /** Check if undo is available */
  canUndo: () => boolean;

  /** Check if redo is available */
  canRedo: () => boolean;

  /** Clear the graph */
  clear: () => void;

  /** Mark as clean (saved) */
  markClean: () => void;

  /** Set nodes (for React Flow callbacks) */
  setNodes: (nodes: Node<HVACNodeData>[]) => void;

  /** Set edges (for React Flow callbacks) */
  setEdges: (edges: Edge<HVACEdgeData>[]) => void;

  /** Get node by ID */
  getNode: (nodeId: string) => Node<HVACNodeData> | undefined;

  /** Get edge by ID */
  getEdge: (edgeId: string) => Edge<HVACEdgeData> | undefined;
}

/**
 * Push an action to the undo stack
 */
function pushUndo(state: HVACState, action: HVACAction): Partial<HVACState> {
  const undoStack = [...state.undoStack, action];
  if (undoStack.length > MAX_HISTORY_SIZE) {
    undoStack.shift();
  }
  return {
    undoStack,
    redoStack: [], // Clear redo stack on new action
    isDirty: true,
  };
}

export const useHVACStore = create<HVACState>()(
  devtools(
    (set, get) => ({
      nodes: [],
      edges: [],
      selectedNodeIds: [],
      selectedEdgeIds: [],
      undoStack: [],
      redoStack: [],
      clipboard: null,
      isDirty: false,
      nodeCounter: new Map(),

      addNode: (componentType: string, position: XYPosition, customName?: string) => {
        const definition = getComponentDefinition(componentType);
        if (!definition) {
          console.error(`Unknown component type: ${componentType}`);
          return '';
        }

        const id = generateNodeId();

        // Generate unique name
        const state = get();
        const counter = (state.nodeCounter.get(componentType) || 0) + 1;
        const name = customName || `${definition.displayName} ${String(counter)}`;

        const newNode: Node<HVACNodeData> = {
          id,
          type: 'hvacNode',
          position,
          data: {
            componentType,
            name,
            fields: { ...definition.defaults },
            hasErrors: false,
            errors: [],
          },
        };

        const action: HVACAction = {
          type: 'ADD_NODE',
          timestamp: Date.now(),
          data: newNode,
          inverse: { nodeId: id },
        };

        set((state) => ({
          nodes: [...state.nodes, newNode],
          nodeCounter: new Map(state.nodeCounter).set(componentType, counter),
          ...pushUndo(state, action),
        }));

        return id;
      },

      removeNode: (nodeId: string) => {
        const state = get();
        const node = state.nodes.find((n) => n.id === nodeId);
        if (!node) return;

        // Also remove connected edges
        const connectedEdges = state.edges.filter(
          (e) => e.source === nodeId || e.target === nodeId
        );

        const action: HVACAction = {
          type: 'REMOVE_NODE',
          timestamp: Date.now(),
          data: { nodeId },
          inverse: { node, edges: connectedEdges },
        };

        set((state) => ({
          nodes: state.nodes.filter((n) => n.id !== nodeId),
          edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
          selectedNodeIds: state.selectedNodeIds.filter((id) => id !== nodeId),
          ...pushUndo(state, action),
        }));
      },

      updateNodePosition: (nodeId: string, position: XYPosition) => {
        set((state) => ({
          nodes: state.nodes.map((n) => (n.id === nodeId ? { ...n, position } : n)),
          isDirty: true,
        }));
      },

      updateNodeData: (nodeId: string, dataUpdates: Partial<HVACNodeData>) => {
        set((state) => ({
          nodes: state.nodes.map((n) =>
            n.id === nodeId ? { ...n, data: { ...n.data, ...dataUpdates } } : n
          ),
          isDirty: true,
        }));
      },

      updateNodePositions: (updates: Array<{ id: string; position: XYPosition }>) => {
        set((state) => ({
          nodes: state.nodes.map((n) => {
            const update = updates.find((u) => u.id === n.id);
            return update ? { ...n, position: update.position } : n;
          }),
          isDirty: true,
        }));
      },

      addEdge: (connection: Connection) => {
        const validation = get().validateConnection(connection);
        if (!validation.valid) {
          console.warn(`Invalid connection: ${validation.reason ?? 'unknown reason'}`);
          return null;
        }

        if (!connection.source || !connection.target || !connection.sourceHandle || !connection.targetHandle) {
          return null;
        }

        const state = get();
        const sourceNode = state.nodes.find((n) => n.id === connection.source);
        const targetNode = state.nodes.find((n) => n.id === connection.target);

        if (!sourceNode || !targetNode) return null;

        const sourceDef = getComponentDefinition(sourceNode.data.componentType);
        const sourcePort = sourceDef?.ports.find((p) => p.id === connection.sourceHandle);

        if (!sourcePort) return null;

        const id = generateEdgeId();
        const nodeName = generateNodeName(
          sourceNode.data.name,
          targetNode.data.name,
          sourcePort.medium
        );

        const newEdge: Edge<HVACEdgeData> = {
          id,
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: getEdgeColor(sourcePort.medium),
            strokeWidth: 2,
          },
          data: {
            sourcePort: connection.sourceHandle,
            targetPort: connection.targetHandle,
            medium: sourcePort.medium,
            nodeName,
          },
        };

        const action: HVACAction = {
          type: 'ADD_EDGE',
          timestamp: Date.now(),
          data: newEdge,
          inverse: { edgeId: id },
        };

        set((state) => ({
          edges: [...state.edges, newEdge],
          ...pushUndo(state, action),
        }));

        return id;
      },

      removeEdge: (edgeId: string) => {
        const edge = get().edges.find((e) => e.id === edgeId);
        if (!edge) return;

        const action: HVACAction = {
          type: 'REMOVE_EDGE',
          timestamp: Date.now(),
          data: { edgeId },
          inverse: { edge },
        };

        set((state) => ({
          edges: state.edges.filter((e) => e.id !== edgeId),
          selectedEdgeIds: state.selectedEdgeIds.filter((id) => id !== edgeId),
          ...pushUndo(state, action),
        }));
      },

      validateConnection: (connection: Connection): ConnectionValidation => {
        const state = get();

        // Check nodes exist
        const sourceNode = state.nodes.find((n) => n.id === connection.source);
        const targetNode = state.nodes.find((n) => n.id === connection.target);

        if (!sourceNode || !targetNode) {
          return { valid: false, reason: 'Source or target node not found' };
        }

        // Get component definitions
        const sourceDef = getComponentDefinition(sourceNode.data.componentType);
        const targetDef = getComponentDefinition(targetNode.data.componentType);

        if (!sourceDef || !targetDef) {
          return { valid: false, reason: 'Component definition not found' };
        }

        // Get ports
        const sourcePort = sourceDef.ports.find((p) => p.id === connection.sourceHandle);
        const targetPort = targetDef.ports.find((p) => p.id === connection.targetHandle);

        if (!sourcePort || !targetPort) {
          return { valid: false, reason: 'Port not found' };
        }

        // Check directions (outlet -> inlet)
        if (sourcePort.direction !== 'outlet') {
          return { valid: false, reason: 'Source must be an outlet port' };
        }

        if (targetPort.direction !== 'inlet') {
          return { valid: false, reason: 'Target must be an inlet port' };
        }

        // Check medium compatibility
        if (sourcePort.medium !== targetPort.medium) {
          return {
            valid: false,
            reason: `Medium mismatch: ${sourcePort.medium} cannot connect to ${targetPort.medium}`,
          };
        }

        // Check for existing connection to target port
        const existingConnection = state.edges.find(
          (e) => e.target === connection.target && e.targetHandle === connection.targetHandle
        );

        if (existingConnection) {
          return { valid: false, reason: 'Target port already has a connection' };
        }

        // Check for self-connection
        if (connection.source === connection.target) {
          return { valid: false, reason: 'Cannot connect a node to itself' };
        }

        return { valid: true };
      },

      selectNodes: (nodeIds: string[]) => {
        set({ selectedNodeIds: nodeIds });
      },

      selectEdges: (edgeIds: string[]) => {
        set({ selectedEdgeIds: edgeIds });
      },

      clearSelection: () => {
        set({ selectedNodeIds: [], selectedEdgeIds: [] });
      },

      deleteSelected: () => {
        const state = get();

        // Delete selected edges first
        for (const edgeId of state.selectedEdgeIds) {
          get().removeEdge(edgeId);
        }

        // Delete selected nodes (which will also remove their edges)
        for (const nodeId of state.selectedNodeIds) {
          get().removeNode(nodeId);
        }
      },

      copySelected: () => {
        const state = get();
        const selectedNodes = state.nodes.filter((n) => state.selectedNodeIds.includes(n.id));

        if (selectedNodes.length === 0) return;

        // Calculate center of selection for relative positioning
        const centerX =
          selectedNodes.reduce((sum, n) => sum + n.position.x, 0) / selectedNodes.length;
        const centerY =
          selectedNodes.reduce((sum, n) => sum + n.position.y, 0) / selectedNodes.length;

        // Create clipboard data
        const nodeMap = new Map(selectedNodes.map((n, i) => [n.id, i]));

        const clipboard: HVACClipboard = {
          nodes: selectedNodes.map((n) => ({
            componentType: n.data.componentType,
            name: n.data.name,
            fields: { ...n.data.fields },
            relativePosition: {
              x: n.position.x - centerX,
              y: n.position.y - centerY,
            },
          })),
          edges: state.edges
            .filter(
              (e) => state.selectedNodeIds.includes(e.source) && state.selectedNodeIds.includes(e.target)
            )
            .filter((e) => e.sourceHandle && e.targetHandle)
            .map((e) => ({
              sourceNodeIndex: nodeMap.get(e.source) ?? 0,
              targetNodeIndex: nodeMap.get(e.target) ?? 0,
              sourcePort: e.sourceHandle ?? '',
              targetPort: e.targetHandle ?? '',
            })),
        };

        set({ clipboard });
      },

      paste: (position?: XYPosition) => {
        const state = get();
        if (!state.clipboard) return;

        const pastePosition = position || { x: 100, y: 100 };
        // Map from original clipboard index to new node ID (only for successful creations)
        const indexToNodeId = new Map<number, string>();

        // Create new nodes
        for (let i = 0; i < state.clipboard.nodes.length; i++) {
          const nodeData = state.clipboard.nodes[i];
          if (!nodeData) continue;

          const newPosition = {
            x: pastePosition.x + nodeData.relativePosition.x,
            y: pastePosition.y + nodeData.relativePosition.y,
          };

          const nodeId = get().addNode(
            nodeData.componentType,
            newPosition,
            `${nodeData.name} (copy)`
          );

          // Only track successfully created nodes (non-empty string)
          if (nodeId) {
            indexToNodeId.set(i, nodeId);
          }
        }

        // Recreate edges between pasted nodes
        for (const edgeData of state.clipboard.edges) {
          const sourceId = indexToNodeId.get(edgeData.sourceNodeIndex);
          const targetId = indexToNodeId.get(edgeData.targetNodeIndex);

          if (sourceId && targetId) {
            get().addEdge({
              source: sourceId,
              target: targetId,
              sourceHandle: edgeData.sourcePort,
              targetHandle: edgeData.targetPort,
            });
          }
        }

        // Select pasted nodes (only successfully created ones)
        const newNodeIds = Array.from(indexToNodeId.values());
        set({ selectedNodeIds: newNodeIds, selectedEdgeIds: [] });
      },

      undo: () => {
        const state = get();
        if (state.undoStack.length === 0) return;

        const action = state.undoStack[state.undoStack.length - 1];
        if (!action) return;
        const inverse = action.inverse as Record<string, unknown>;

        // Apply inverse action
        switch (action.type) {
          case 'ADD_NODE':
            set((s) => ({
              nodes: s.nodes.filter((n) => n.id !== (inverse['nodeId'] as string)),
            }));
            break;

          case 'REMOVE_NODE':
            set((s) => ({
              nodes: [...s.nodes, inverse['node'] as Node<HVACNodeData>],
              edges: [...s.edges, ...(inverse['edges'] as Edge<HVACEdgeData>[])],
            }));
            break;

          case 'ADD_EDGE':
            set((s) => ({
              edges: s.edges.filter((e) => e.id !== (inverse['edgeId'] as string)),
            }));
            break;

          case 'REMOVE_EDGE':
            set((s) => ({
              edges: [...s.edges, inverse['edge'] as Edge<HVACEdgeData>],
            }));
            break;
        }

        // Move action to redo stack
        set((s) => ({
          undoStack: s.undoStack.slice(0, -1),
          redoStack: [...s.redoStack, action],
          isDirty: true,
        }));
      },

      redo: () => {
        const state = get();
        if (state.redoStack.length === 0) return;

        const action = state.redoStack[state.redoStack.length - 1];
        if (!action) return;
        const data = action.data as Record<string, unknown>;

        // Reapply action
        switch (action.type) {
          case 'ADD_NODE':
            set((s) => ({
              nodes: [...s.nodes, data as unknown as Node<HVACNodeData>],
            }));
            break;

          case 'REMOVE_NODE':
            set((s) => ({
              nodes: s.nodes.filter((n) => n.id !== (data['nodeId'] as string)),
              edges: s.edges.filter(
                (e) => e.source !== (data['nodeId'] as string) && e.target !== (data['nodeId'] as string)
              ),
            }));
            break;

          case 'ADD_EDGE':
            set((s) => ({
              edges: [...s.edges, data as unknown as Edge<HVACEdgeData>],
            }));
            break;

          case 'REMOVE_EDGE':
            set((s) => ({
              edges: s.edges.filter((e) => e.id !== (data['edgeId'] as string)),
            }));
            break;
        }

        // Move action back to undo stack
        set((s) => ({
          redoStack: s.redoStack.slice(0, -1),
          undoStack: [...s.undoStack, action],
          isDirty: true,
        }));
      },

      canUndo: () => get().undoStack.length > 0,

      canRedo: () => get().redoStack.length > 0,

      clear: () => {
        set({
          nodes: [],
          edges: [],
          selectedNodeIds: [],
          selectedEdgeIds: [],
          undoStack: [],
          redoStack: [],
          isDirty: false,
          nodeCounter: new Map(),
        });
      },

      markClean: () => {
        set({ isDirty: false });
      },

      setNodes: (nodes: Node<HVACNodeData>[]) => {
        set({ nodes, isDirty: true });
      },

      setEdges: (edges: Edge<HVACEdgeData>[]) => {
        set({ edges, isDirty: true });
      },

      getNode: (nodeId: string) => {
        return get().nodes.find((n) => n.id === nodeId);
      },

      getEdge: (edgeId: string) => {
        return get().edges.find((e) => e.id === edgeId);
      },
    }),
    { name: 'HVACStore' }
  )
);

/**
 * Get edge color based on fluid medium
 */
function getEdgeColor(medium: FluidMedium): string {
  switch (medium) {
    case 'air':
      return '#64B5F6'; // Light blue
    case 'water':
      return '#42A5F5'; // Blue
    case 'refrigerant':
      return '#AB47BC'; // Purple
    case 'steam':
      return '#BDBDBD'; // Gray
    default:
      return '#90A4AE';
  }
}

/**
 * Selector for selected nodes
 */
export const useSelectedNodes = () => {
  const nodes = useHVACStore((state) => state.nodes);
  const selectedNodeIds = useHVACStore((state) => state.selectedNodeIds);
  return nodes.filter((n) => selectedNodeIds.includes(n.id));
};

/**
 * Selector for selected edges
 */
export const useSelectedEdges = () => {
  const edges = useHVACStore((state) => state.edges);
  const selectedEdgeIds = useHVACStore((state) => state.selectedEdgeIds);
  return edges.filter((e) => selectedEdgeIds.includes(e.id));
};
